import React, { useEffect, useState } from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import Product from 'classes/Product.js';
import { useServiceability } from 'views/ServiceabilityProvider.js';
import LimitedOfferBanner from 'views/LimitedOfferBanner.js';

const Header = ({ cartCount, customer, onAccountClick, onCartClick, onNavigate }) => {

    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [productsOpen, setProductsOpen] = useState(false);

    // Cart + Shop sign-in are online-sales features — hidden in gated areas.
    const { canBuyOnline } = useServiceability();

    // Individual products for the "Products" dropdown → each links to its page
    // Bundles (e.g. the Base Network) listed first.
    const productLinks = [...Product.getAll()]
        .sort((a, b) => (b.bundle ? 1 : 0) - (a.bundle ? 1 : 0))
        .map(p => ({ label: p.title, subtitle: p.subtitle, handle: p.handle }));

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { label: 'Home', section: 'hero' },
        { label: 'Products', products: true },
        { label: 'About Us', route: '/about' },
        { label: 'Careers', route: '/careers' },
        { label: 'Contact', route: '/contact' },
    ];

    // A link either routes to a page ({ route }) or scrolls to a homepage
    // section ({ section }). App.handleNavigate resolves both.
    const handleNavClick = (link) => {
        setMobileMenuOpen(false);
        if(onNavigate) {
            onNavigate(link.route ? { route: link.route } : { section: link.section });
        }
    };

    const handleAccount = () => {
        // Logged out: starts "Sign in with Shop". Logged in: opens account drawer.
        setMobileMenuOpen(false);
        if(onAccountClick) {
            onAccountClick();
        }
    };

    return (
        <>
            <header style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                backgroundColor: 'rgba(255,255,255,0.97)', //scrolled ? 'rgba(255,255,255,0.97)' : 'transparent',
                backdropFilter: scrolled ? 'blur(20px)' : 'none',
                boxShadow: scrolled ? Appearance.boxShadow.nav : 'none',
                transition: 'all 0.3s ease',
            }}>
                <LimitedOfferBanner onClick={() => handleNavClick({ section: 'products' })} />
                {/* Three fixed columns rather than space-between: the outer columns share the
                    leftover width evenly, so the logo and nav hold their positions whether or not
                    the shop + cart buttons have rendered. With space-between the nav was centered
                    between its neighbors, so it slid left the moment serviceability resolved. */}
                {/* Pinned to the shared header height so the row cannot resize with its contents —
                    see Appearance.layout for why, and change the geometry there rather than here. */}
                <div style={{
                    maxWidth: Appearance.layout.maxWidth,
                    margin: '0 auto',
                    padding: Appearance.layout.headerPadding(),
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center',
                    minHeight: Appearance.layout.headerHeight(),
                }}>

                    {/* Logo */}
                    <div
                        onClick={() => handleNavClick({ section: 'hero' })}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            cursor: 'pointer',
                            justifySelf: 'start',
                        }}>
                        <img 
                        src={'/images/aqua-link-dark.png'}
                        style={{
                            height: 25,
                            objectFit: 'contain',
                            width: 'auto'
                        }} />
                    </div>

                    {/* Desktop Navigation */}
                    {!Utils.isMobile() && (
                        <nav style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 32,
                        }}>
                            {navLinks.map((link, index) => {
                                if(link.products) {
                                    return (
                                        <div
                                            key={index}
                                            onMouseEnter={() => setProductsOpen(true)}
                                            onMouseLeave={() => setProductsOpen(false)}
                                            style={{ position: 'relative' }}>
                                            <span style={Appearance.textStyles.navLink({
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                            })}>
                                                {'Products'}
                                                <i className={'fas fa-chevron-down'} style={{ fontSize: 9, opacity: 0.55 }} />
                                            </span>

                                            {productsOpen && (
                                                <div style={{ position: 'absolute', top: '100%', left: -16, paddingTop: 14 }}>
                                                    <div style={{
                                                        minWidth: 260,
                                                        backgroundColor: Appearance.colors.white,
                                                        border: `1px solid ${Appearance.colors.border()}`,
                                                        borderRadius: 16,
                                                        boxShadow: Appearance.boxShadow.cardHover,
                                                        padding: 8,
                                                    }}>
                                                        {productLinks.map((pl, i) => (
                                                            <div
                                                                key={i}
                                                                onClick={() => handleNavClick({ route: `/products/${pl.handle}` })}
                                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = Appearance.colors.offWhite; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                                style={{ padding: '12px 14px', borderRadius: 10, cursor: 'pointer', transition: 'background-color 0.15s ease' }}>
                                                                <div style={{ ...Appearance.textStyles.navLink({ fontSize: 15, fontWeight: 600 }), marginBottom: 2 }}>
                                                                    {pl.label}
                                                                </div>
                                                                <div style={Appearance.textStyles.caption({ color: Appearance.colors.subText() })}>
                                                                    {pl.subtitle}
                                                                </div>
                                                            </div>
                                                        ))}

                                                        {/* Divider */}
                                                        <div style={{ height: 1, backgroundColor: Appearance.colors.border(), margin: '6px 8px' }} />

                                                        {/* Guided quiz */}
                                                        <div
                                                            onClick={() => handleNavClick({ route: '/build-your-system' })}
                                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = Appearance.colors.offWhite; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                            style={{ padding: '12px 14px', borderRadius: 10, cursor: 'pointer', transition: 'background-color 0.15s ease' }}>
                                                            <div style={Appearance.textStyles.navLink({ fontSize: 15, fontWeight: 600, color: Appearance.colors.primary() })}>
                                                                {'Find Your Solution ›'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <span
                                        key={index}
                                        onClick={() => handleNavClick(link)}
                                        style={Appearance.textStyles.navLink({
                                            position: 'relative',
                                        })}>
                                        {link.label}
                                    </span>
                                );
                            })}
                        </nav>
                    )}

                    {/* Holds the middle column on mobile, where the nav links are hidden — without
                        it the right actions would fall into the center column instead of the right */}
                    {Utils.isMobile() && (
                        <div />
                    )}

                    {/* Right Actions */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 20,
                        justifySelf: 'end',
                    }}>
                        {canBuyOnline && !Utils.isMobile() && (
                            customer ? (
                                <span
                                    onClick={handleAccount}
                                    style={{
                                        ...Appearance.textStyles.navLink(),
                                        fontSize: 14,
                                        color: Appearance.colors.text(),
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}>
                                    <i className={'fas fa-user-circle'} style={{ fontSize: 16 }} />
                                    {customer.firstName || 'Account'}
                                </span>
                            ) : (
                                <ShopSignInButton onClick={handleAccount} />
                            )
                        )}

                        {/* Cart — only when online sales are supported */}
                        {canBuyOnline && (
                            <div
                                onClick={onCartClick}
                                style={{
                                    position: 'relative',
                                    width: 40,
                                    height: 40,
                                    borderRadius: 20,
                                    backgroundColor: Appearance.colors.dark(),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease',
                                }}>
                                <i className={'fas fa-shopping-bag'} style={{ color: 'white', fontSize: 14 }} />
                                {cartCount > 0 && (
                                    <div className={'cart-badge'}>
                                        {cartCount}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Mobile Menu Toggle */}
                        {Utils.isMobile() && (
                            <div
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                style={{
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                }}>
                                <i className={mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'} style={{ fontSize: 18 }} />
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Mobile Menu */}
            {Utils.isMobile() && mobileMenuOpen && (
                <div style={{
                    position: 'fixed',
                    top: `calc(${Appearance.layout.headerHeight()}px + var(--offer-banner-height, 0px))`,
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(255,255,255,0.98)',
                    backdropFilter: 'blur(20px)',
                    zIndex: 99,
                    padding: '20px',
                    borderBottom: `1px solid ${Appearance.colors.border()}`,
                    animation: 'slideDown 0.3s ease',
                }}>
                    {navLinks.map((link, index) => {
                        if(link.products) {
                            return (
                                <div key={index}>
                                    <div style={{
                                        padding: '14px 0 8px',
                                        ...Appearance.textStyles.caption({ color: Appearance.colors.subText(), textTransform: 'uppercase', letterSpacing: '0.06em' }),
                                    }}>
                                        {'Products'}
                                    </div>
                                    {productLinks.map((pl, i) => (
                                        <div
                                            key={i}
                                            onClick={() => handleNavClick({ route: `/products/${pl.handle}` })}
                                            style={{
                                                padding: '12px 0 12px 14px',
                                                borderBottom: `1px solid ${Appearance.colors.border()}`,
                                                cursor: 'pointer',
                                                ...Appearance.textStyles.navLink({ fontSize: 16 }),
                                            }}>
                                            {pl.label}
                                        </div>
                                    ))}
                                </div>
                            );
                        }
                        return (
                            <div
                                key={index}
                                onClick={() => handleNavClick(link)}
                                style={{
                                    padding: '14px 0',
                                    borderBottom: `1px solid ${Appearance.colors.border()}`,
                                    cursor: 'pointer',
                                    ...Appearance.textStyles.navLink({ fontSize: 16 }),
                                }}>
                                {link.label}
                            </div>
                        );
                    })}
                    {canBuyOnline && (
                        <div
                            onClick={handleAccount}
                            style={{
                                padding: '14px 0',
                                cursor: 'pointer',
                                ...Appearance.textStyles.navLink({ fontSize: 16 }),
                            }}>
                            {customer ? (customer.firstName ? `${customer.firstName}'s Account` : 'My Account') : 'Sign in with Shop'}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

// Branded "Sign in with Shop" button (Shop purple)
const ShopSignInButton = ({ onClick }) => (
    <button
        onClick={onClick}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '9px 16px',
            borderRadius: 20,
            border: 'none',
            cursor: 'pointer',
            backgroundColor: '#5A31F4',
            color: 'white',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: Appearance.fonts.body,
        }}>
        <i className={'fas fa-shopping-bag'} style={{ fontSize: 13 }} />
        {'Sign in with Shop'}
    </button>
);

export default Header;
